import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { useSubscriptionInvoices } from '../../../hooks/useSubscription';

function fmtVnd(n: number) {
  return new Intl.NumberFormat('vi-VN').format(n) + 'đ';
}
/**
 * `periodStart`, `periodEnd` and `paidAt` are true UTC instants — NOT the
 * house-frame wall clock — so they format through an explicit
 * Asia/Ho_Chi_Minh timezone, the same way SubscriptionTab's fmtDate does. A
 * bare toLocaleDateString prints the viewer's own day, which can disagree
 * with the invoice period shown.
 */
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
}

/** Invoice status is a three-value enum (PENDING | PAID | EXPIRED) declared on
 *  the Prisma model, and it is NOT the subscription's status enum — hence its
 *  own `invoiceStatus.*` namespace. Colours follow the system: ochre is the
 *  earned 10% for money that needs action, brand for settled, grey for spent.
 *  An unknown value falls back to printing itself rather than an empty chip, so
 *  a future enum member is visible instead of silent. */
const INVOICE_STATUS_CLASS: Record<string, string> = {
  PENDING: 'bg-attention-tint text-attention-foreground',
  PAID: 'bg-brand-50 text-brand-700',
  EXPIRED: 'bg-surface-100 text-surface-500',
};

function InvoiceStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('billing');
  const cls = INVOICE_STATUS_CLASS[status] ?? 'bg-surface-100 text-surface-500';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-[12px] font-medium ${cls}`}>
      {t(`invoiceStatus.${status}`, { defaultValue: status })}
    </span>
  );
}

export function InvoicesTab() {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const { data, isLoading, error } = useSubscriptionInvoices();

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="overflow-hidden rounded-[12px] border border-surface-200 bg-surface-0">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className={`flex items-center justify-between gap-[12px] p-[16px] ${i > 0 ? 'border-t border-surface-100' : ''}`}
            >
              <div className="space-y-[6px]">
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-red-700 bg-red-50 border border-red-200 rounded-md max-w-2xl">
        {(error as Error).message}
      </div>
    );
  }

  // The empty state says what this list is FOR, because a teacher who has never
  // bought sees it before they see anything else on the tab.
  if (!data || data.length === 0) {
    return (
      <div className="p-6">
        <div className="rounded-[12px] border border-surface-200 bg-surface-0 p-[16px]">
          <p className="text-[13px] font-medium text-surface-700">{t('invoicesEmpty')}</p>
          <p className="mt-[6px] text-[12.5px] leading-[1.6] text-surface-500">{t('invoicesEmptyHint')}</p>
        </div>
      </div>
    );
  }

  // One ledger card with hairline rows, the same grammar as SubscriptionTab's
  // status card — not a bordered table, and never the landing's floating cards
  // (INVARIANTS §6). Rows are <button> so the whole row is one real, keyboard-
  // reachable target; the old <tr onClick> was neither.
  return (
    <div className="p-6">
      <div className="overflow-hidden rounded-[12px] border border-surface-200 bg-surface-0">
        {data.map((inv, i) => (
          <button
            key={inv.id}
            type="button"
            onClick={() => navigate(`/settings/billing/invoices/${inv.id}`)}
            className={`flex w-full min-h-[64px] items-center justify-between gap-[12px] px-[16px] py-[12px] text-left transition-colors hover:bg-surface-50 ${
              i > 0 ? 'border-t border-surface-100' : ''
            }`}
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-[8px]">
                <span className="font-mono text-[12px] text-surface-500">{inv.paymentRef}</span>
                <InvoiceStatusBadge status={inv.status} />
              </div>
              <p className="mt-[4px] text-[13px] text-surface-600">
                {fmtDate(inv.periodStart)} → {fmtDate(inv.periodEnd)}
              </p>
              {inv.paidAt && (
                <p className="mt-[2px] text-[12px] text-surface-400">
                  {t('invoicePaidOn', { date: fmtDate(inv.paidAt) })}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-[8px]">
              <span className="tabular text-[15px] font-semibold text-surface-900">
                {fmtVnd(inv.amountVnd)}
              </span>
              <ChevronRight className="h-4 w-4 text-surface-400" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
